/**
 * Holdings query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { holdingsApi, HoldingsListParams, BulkTagAction } from '../api/holdings'
import type { HoldingCreate, HoldingUpdate } from '../types'
import { TAGS_KEY } from './useTags'

export const HOLDINGS_KEY = ['holdings']

export interface UseHoldingsParams {
  accountId?: string
  portfolioId?: string
  tagIds?: string[]
}

export function useHoldings(params?: UseHoldingsParams) {
  const queryKey = params
    ? [...HOLDINGS_KEY, params]
    : HOLDINGS_KEY

  return useQuery({
    queryKey,
    queryFn: () => holdingsApi.list(params as HoldingsListParams),
  })
}

export function useHolding(holdingId: string) {
  return useQuery({
    queryKey: [...HOLDINGS_KEY, holdingId],
    queryFn: () => holdingsApi.get(holdingId),
    enabled: !!holdingId,
  })
}

export function useCreateHolding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: HoldingCreate) => holdingsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
    },
  })
}

export function useUpdateHolding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdingId, data }: { holdingId: string; data: HoldingUpdate }) =>
      holdingsApi.update(holdingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
    },
  })
}

export function useDeleteHolding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (holdingId: string) => holdingsApi.delete(holdingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
    },
  })
}

// Bulk operations

export function useBulkUpdateHoldingTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      holdingIds,
      action,
      tagIds,
    }: {
      holdingIds: string[]
      action: BulkTagAction
      tagIds: string[]
    }) => holdingsApi.bulkUpdateTags(holdingIds, action, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}

export function useBulkUpdateHoldingPortfolio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      holdingIds,
      portfolioId,
    }: {
      holdingIds: string[]
      portfolioId: string
    }) => holdingsApi.bulkUpdatePortfolio(holdingIds, portfolioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
    },
  })
}

export function useBulkDeleteHoldings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (holdingIds: string[]) => holdingsApi.bulkDelete(holdingIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
    },
  })
}
